import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, ShieldCheck, Users, BarChart3, Sparkles, CheckCircle2 } from 'lucide-react';
import LeftContentRightImage from '../components/projects/LeftContentRightImage'
import styles from './ProductPage.module.css';
import ImageCarousel from '../components/projects/ImageCarousel';
import Dashboard from '../assets/project-imgs/Dashboards.png'
import Members from '../assets/project-imgs/Memberss.png'
import Payments from '../assets/project-imgs/Payments.png'
import Report2 from '../assets/project-imgs/Reports1.png'
import Report1 from '../assets/project-imgs/Reports2.png'
import Complaint from '../assets/project-imgs/Complaints.png'
import ComplainResolve from '../assets/project-imgs/ComplaintResolve.png'
import PricingContent from '../components/projects/PricingContent';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

const ProductPage = ({ setIsRegisterOpen }) => {
  const [activeFeatureTab, setActiveFeatureTab] = useState(0);

  const tabbedFeatures = [
    { 
      title: "Admin Command", 
      desc: "A centralized command center to manage flat owners, tenants, and unit mapping with total digital transparency.", 
      slides: [
        { title: "Dashboard Overview", imgUrl: Dashboard }, 
        { title: "Resident Directory", imgUrl: Members },
        // { title: "Asset Management", imgUrl: ComplainResolve }
      ] 
    },
    { 
      title: "Finance & Payments", 
      desc: "Master your manual monthly cycles. Log cash/cheque payments, manage maintenance dues, and track the society treasury.", 
      slides: [
        { title: "Payment Ledger", imgUrl: Payments },
        // { title: "Invoicing", imgUrl: Report2 }
      ] 
    },
    { 
      title: "Resident Helpdesk", 
      desc: "Empower residents to voice concerns. Track, assign, and resolve maintenance complaints with a structured ticket system.", 
      slides: [
        { title: "Complaint Portal", imgUrl: Complaint },
        // { title: "Resolution Tracking", imgUrl: ComplainResolve }
      ] 
    }
  ];

  return (
    <>
      <LeftContentRightImage setIsRegisterOpen={setIsRegisterOpen} />
      <div className={styles.pageContainer}>
        
        {/* --- 1. HERO SECTION --- */}
        <section className={styles.heroSection}>
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className={styles.heroContent}>
            <h1>The Operating System for <br /><span className={styles.blueText}>Modern Communities</span></h1>
            <p className={styles.heroSubtext}>
              Eliminate manual paperwork. Our platform helps apartment committees automate member registration, 
              track maintenance payments, and resolve resident complaints in one elegant workspace.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{delay: 0.3}} className={styles.heroFormContainer}>
            {/* Badges/Forms are commented out as per your previous structure */}
          </motion.div>
        </section>

        <PricingContent  setIsRegisterOpen={setIsRegisterOpen} />

        {/* --- 3. TABBED CAROUSEL SECTION --- */}
        <section className={styles.tabbedSection}>
          <motion.div 
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className={styles.sectionHeader}
          >
            <h2>Comprehensive Management Modules</h2>
            <p>From the front gate to the accounts office, give every stakeholder the tools they need to maintain a harmonious society.</p>
          </motion.div>

          <div className={styles.tabsContainer}>
            <div className={styles.tabButtons}>
              {tabbedFeatures.map((tab, index) => (
                <button 
                  key={index}
                  className={`${styles.tabBtn} ${activeFeatureTab === index ? styles.activeTab : ''}`}
                  onClick={() => setActiveFeatureTab(index)}
                >
                  {tab.title}
                </button>
              ))}
            </div>
            <div className={styles.tabContentDesc}>
              <p>{tabbedFeatures[activeFeatureTab].desc}</p>
            </div>

            <ImageCarousel slides={tabbedFeatures[activeFeatureTab].slides} />
          </div>
        </section>

        {/* --- 4. TESTIMONIALS SECTION --- */}
<section className={styles.testimonialsSection}>
  <div className={styles.sectionHeader}>
    <h2>Trusted by Management Committees</h2>
  </div>
  <div className={styles.testimonialGrid}>
    {// Inside your array in the JSX:
[
  { 
    name: "Arun Kumar", 
    role: "Secretary, Green Valley Apts", 
    quote: "Tracking manual cash payments was a nightmare before this. Now, our ledger is always accurate and transparent.",
    avatarUrl: "https://ui-avatars.com/api/?name=Arun+Kumar&background=0D8ABC&color=fff&rounded=true&bold=true" 
  },
  { 
    name: "Sarah Malik", 
    role: "Treasurer, Skyview Residency", 
    quote: "The expense calculator saved us hours of spreadsheet work. Distributing miscellaneous costs is now mathematically perfect.",
    avatarUrl: "https://ui-avatars.com/api/?name=Sarah+Malik&background=F44336&color=fff&rounded=true&bold=true" 
  },
  { 
    name: "Prakash Raj", 
    role: "President, Urban Hive", 
    quote: "Complaint resolution time dropped by 60%. The residents feel heard, and the staff knows exactly what to do.",
    avatarUrl: "https://ui-avatars.com/api/?name=Prakash+Raj&background=4CAF50&color=fff&rounded=true&bold=true" 
  },
  { 
    name: "David Wilson", 
    role: "Manager, Grand Plaza", 
    quote: "Financial reporting for our annual general meeting used to take weeks. Now it's just a single click export.",
    avatarUrl: "https://ui-avatars.com/api/?name=David+Wilson&background=FF9800&color=fff&rounded=true&bold=true" 
  }
].map((item, index) => (
      <motion.div whileHover={{ y: -10 }} className={styles.testiCard} key={index}>
        <div className={styles.quoteIcon}>“</div>
        <p>{item.quote}</p>
        <div className={styles.testiUser}>
          <img 
            src={item.avatarUrl} 
            alt={item.name} 
            className={styles.userAvatar} 
          />
          <div>
            <strong>{item.name}</strong><br/><span>{item.role}</span>
          </div>
        </div>
      </motion.div>
    ))}
  </div>
</section>

        {/* --- 5. AI SECTION --- */}
        <section className={styles.aiSection}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <Sparkles size={60} className={styles.aiIcon} />
            <h2>Smarter Community Governance</h2>
            <p>Leverage intelligent sorting for complaints and automated budget forecasting to keep your society's maintenance funds healthy and optimized.</p>
          </motion.div>
        </section>

        {/* --- 6. SPLIT FEATURE SECTION (Financial Intelligence) --- */}
        <section className={styles.splitSection}>
          <div className={styles.container}>
            <div className={styles.splitLayoutLeftText}>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className={styles.splitTextContent}>
                <h2>Financial Clarity, <span className={styles.blueText}>Everywhere.</span></h2>
                <p>
                  Access deep financial insights whenever and wherever you are. Our reporting suite handles the complex math 
                  of building operations so you don't have to.
                </p>
                
                <ul className={styles.checklist}>
                  <li><CheckCircle2 size={18} /> <strong>Automated Defaulter Lists:</strong> Spot pending dues instantly.</li>
                  <li><CheckCircle2 size={18} /> <strong>Expense Share Logic:</strong> Fair calculation of miscellaneous costs.</li>
                  <li><CheckCircle2 size={18} /> <strong>Audit-Ready Ledgers:</strong> Professional reports for committee meetings.</li>
                </ul>
              </motion.div>
              <div className={styles.splitImageContent}>
                 <ImageCarousel slides={[
                   {title: "Expense Report", imgUrl: Report1}, 
                   {title: "Collection Summary", imgUrl: Report2}
                 ]} />
              </div>
            </div>
          </div>
        </section>

        {/* --- 7. GROWTH STATS --- */}
        <section className={styles.footerSection}>
          <div className={styles.growthHeader}>
             <h2>The Impact of Digital Management</h2>
          </div>
          <div className={styles.statsGrid}>
            <motion.div initial="hidden" whileInView="visible" variants={fadeInUp} className={styles.statCard} style={{background: '#e0e7ff'}}>
              <h3>95%</h3>
              <p>Collection Efficiency</p>
              <small>Timely maintenance recovery</small>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" variants={fadeInUp} transition={{delay:0.2}} className={styles.statCard} style={{background: '#cffafe'}}>
              <h3>40%</h3>
              <p>Faster Resolution</p>
              <small>Quicker complaint turnaround</small>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" variants={fadeInUp} transition={{delay:0.4}} className={styles.statCard} style={{background: '#ffedd5'}}>
              <h3>100%</h3>
              <p>Financial Transparency</p>
              <small>Zero errors in ledger balance</small>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default ProductPage;

// 0-5 1admin 1499-20 2admin 3999--1 5admin